import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { api } from "@/services/api";
import PracticeBoard from "./PracticeBoard";
import type { PracticePuzzle } from "./catalog";
import PuzzleDifficultyPopup from "./PuzzleDifficultyPopup";
import {
  PuzzleDifficultyKey,
  readPuzzleDifficulty,
  writePuzzleDifficulty,
} from "./puzzleDifficulty";
import useTranslation from "next-translate/useTranslation";

const STARTING_PUZZLE_RATING = 800;
const GUEST_PUZZLE_RATING_STORAGE_KEY = "puzzle_rating_guest_v2";

function readGuestPuzzleRating() {
  if (typeof window === "undefined") return STARTING_PUZZLE_RATING;
  try {
    const rating = Number(
      window.localStorage.getItem(GUEST_PUZZLE_RATING_STORAGE_KEY),
    );
    return Number.isFinite(rating) && rating > 0
      ? Math.round(rating)
      : STARTING_PUZZLE_RATING;
  } catch {
    return STARTING_PUZZLE_RATING;
  }
}

async function fetchPracticePuzzle(
  theme: string,
  playedPuzzleIds: string[],
  difficulty: PuzzleDifficultyKey,
) {
  const response = await api.post(
    "/puzzle-practice/next",
    {
      theme,
      difficulty,
      elo: readGuestPuzzleRating(),
      playedPuzzleIds: playedPuzzleIds.slice(-100),
    },
    { timeout: 15000 },
  );
  return response.data.puzzle as PracticePuzzle;
}

export default function CategoryTrainer({ theme }: { theme: string }) {
  const { t } = useTranslation("puzzles");
  const [puzzle, setPuzzle] = useState<PracticePuzzle | null>(null);
  const [played, setPlayed] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [difficulty, setDifficulty] = useState<PuzzleDifficultyKey>("balanced");
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [difficultyOpen, setDifficultyOpen] = useState(false);
  const [prefetchedPuzzle, setPrefetchedPuzzle] =
    useState<PracticePuzzle | null>(null);
  const initialRequest = useRef<{
    key: string;
    promise: Promise<PracticePuzzle>;
  } | null>(null);
  const prefetchRequest = useRef<Promise<PracticePuzzle> | null>(null);

  useEffect(() => {
    setDifficulty(readPuzzleDifficulty());
    setPreferencesReady(true);
  }, []);

  useEffect(() => {
    if (!preferencesReady) return;
    setPuzzle(null);
    setPlayed([]);
    setLoading(true);
    setError(false);
    setPrefetchedPuzzle(null);
    prefetchRequest.current = null;
    const requestKey = `${theme}:${difficulty}`;
    const request =
      initialRequest.current?.key === requestKey
        ? initialRequest.current.promise
        : fetchPracticePuzzle(theme, [], difficulty);
    initialRequest.current = { key: requestKey, promise: request };
    request
      .then((nextPuzzle) => {
        if (initialRequest.current?.promise !== request) return;
        setPuzzle(nextPuzzle);
        setPlayed([nextPuzzle.id]);
      })
      .catch(() => {
        if (initialRequest.current?.promise === request) setError(true);
      })
      .finally(() => {
        if (initialRequest.current?.promise === request) setLoading(false);
      });
  }, [difficulty, preferencesReady, theme]);

  useEffect(() => {
    if (!puzzle || loading || prefetchedPuzzle || prefetchRequest.current) {
      return;
    }
    const request = fetchPracticePuzzle(theme, played, difficulty);
    prefetchRequest.current = request;
    request
      .then((nextPuzzle) => {
        if (prefetchRequest.current === request)
          setPrefetchedPuzzle(nextPuzzle);
      })
      .catch(() => {
        // Preloading is opportunistic; an ordinary request is still available.
      })
      .finally(() => {
        if (prefetchRequest.current === request) prefetchRequest.current = null;
      });
  }, [difficulty, loading, played, prefetchedPuzzle, puzzle, theme]);

  async function next() {
    setLoading(true);
    setError(false);
    try {
      const pendingPrefetch = prefetchRequest.current;
      const nextPuzzle =
        prefetchedPuzzle ||
        (pendingPrefetch
          ? await pendingPrefetch
          : await fetchPracticePuzzle(theme, played, difficulty));
      setPrefetchedPuzzle(null);
      setPuzzle(nextPuzzle);
      setPlayed((ids) => [...ids.slice(-99), nextPuzzle.id]);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  function changeDifficulty(nextDifficulty: PuzzleDifficultyKey) {
    if (nextDifficulty === difficulty) return;
    writePuzzleDifficulty(nextDifficulty);
    setPuzzle(null);
    setPlayed([]);
    setPrefetchedPuzzle(null);
    prefetchRequest.current = null;
    setLoading(true);
    setError(false);
    setDifficulty(nextDifficulty);
  }

  if (!puzzle) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-lg bg-athens-gray-150 px-6 text-center text-sm text-slate-600">
        {loading ? (
          <p role="status">{t("general.loading")}</p>
        ) : (
          <div>
            <p role="alert">{t("themes.loadError")}</p>
            <Button
              className="mt-4"
              variant="default"
              size="default"
              type="button"
              onClick={next}
            >
              {t("general.retry")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <PracticeBoard
        key={puzzle.id}
        puzzle={puzzle}
        onNext={next}
        nextLoading={loading}
        onOpenDifficultySettings={() => setDifficultyOpen(true)}
      />
      {error && (
        <p role="alert" className="mt-4 text-sm text-red-700">
          {t("themes.loadError")}{" "}
          <Button
            variant="quiet"
            size="default"
            type="button"
            onClick={next}
            disabled={loading}
          >
            {t("general.retry")}
          </Button>
        </p>
      )}
      {difficultyOpen && (
        <PuzzleDifficultyPopup
          selectedDifficulty={difficulty}
          onSelect={changeDifficulty}
          onClose={() => setDifficultyOpen(false)}
          t={t}
        />
      )}
    </>
  );
}
